<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { ControlsContainer, LabelListV2, SliderV2, Text } from '@Shelly/shelly-design-system';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { type SettingRequest } from 'src/types/requests/settings/setting';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	//Components
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import VirtualButton from 'src/components/DevicesSettings/VirtualComponents/Components/VirtualButton.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { VCInterfaceStatus, VirtualComponentInterfaceData } from 'src/types/VirtualComponents';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	export let device: Device, statuses: VCInterfaceStatus[];

	let isLoading: boolean = false;
	let valueBeforeSend: number = null;
	let statusUp: VirtualComponentInterfaceData;
	let statusDown: VirtualComponentInterfaceData;
	let statusPosition: VirtualComponentInterfaceData;
	let value: number = 0;

	$: if (statuses) {
		statusUp = statuses.find((i) => /^(up|open)$/.test(i.role));
		statusDown = statuses.find((i) => /^(down|close)$/.test(i.role));
		statusPosition = statuses.find((i) => /^(position)$/.test(i.role));
		value = valueBeforeSend !== null ? valueBeforeSend : statusPosition?.event || 0;
		if (!isLoading && valueBeforeSend !== null && statusPosition?.event == valueBeforeSend) {
			valueBeforeSend = null;
		}
	}

	const changePosition = async (value: number) => {
		isLoading = true;
		valueBeforeSend = value;
		const res: SettingRequest = await gen2Request(device, 'Number.set', {
			id: statusPosition.id,
			value,
		});
		if (!res?.isok) {
			valueBeforeSend = null;
			onSettingsResponse(res, device);
		}
		isLoading = false;
	};

	const a11yProps = {
		ariaLabel: $format('label_valve_position', { default: 'Valve Position' }),
		ariaLabelValue:
			$format('label_valve_position', { default: 'Valve Position' }) + ' ' + (value || 0) + '%',
		role: 'slider',
		'aria-valuemin': statusPosition?.min || 0,
		'aria-valuemax': statusPosition?.max || 100,
		'aria-valuenow': value || 0,
		'aria-live': 'polite',
	};

	$: filteredStatuses = statuses.filter(
		(i) => /^(last_direction|has_power)$/.test(i.role) || i?.sensor,
	);
</script>

<div class="flex-full-height justify-content-end">
	<DeviceLabelList labelList={filteredStatuses} />
	<DeviceNotificationLabel deviceId={device?.id} />

	{#if statusPosition?.id}
		<div class="flex-full-height">
			<SliderV2
				id="slider-uf6eepf"
				{a11yProps}
				class="flex-full-height justify-content-center"
				title={value == 0
					? capFirstChar($format(`slider_title_state_closing`, { default: 'closing' }))
					: capFirstChar($format('slider_title_state_opened', { default: 'opened' }))}
				{value}
				step={Number(statusPosition?.step || 1)}
				on:value_change={(e) => {
					changePosition(e.detail.value);
				}}
				unit={statusPosition.unit || ''}
				{isLoading}
			/>
		</div>
	{/if}

	{#if statusUp?.id && statusDown?.id}
		<div class="justify-content-end{statusPosition?.id ? '' : ' flex-full-height'}">
			<ControlsContainer class="mb-xs" color="color-0">
				<svelte:fragment slot="first">
					<VirtualButton
						{device}
						hasDarkIcon
						componentID={statusUp.id}
						value={statusUp.event}
						useV2Components
						icon="fa fa-arrow-up"
						size="l"
					/>
					<Text styleType="controls-large" class="mt-s text-bold"
						>{$format('text_device_control_direction_open', {
							default: 'Open',
						})}</Text
					>
				</svelte:fragment>
				<svelte:fragment slot="second">
					<VirtualButton
						{device}
						hasDarkIcon
						componentID={statusDown.id}
						value={statusDown.event}
						useV2Components
						icon="fa fa-arrow-down"
						size="l"
					/>
					<Text styleType="controls-large" class="mt-s text-bold"
						>{$format('text_device_control_direction_close', {
							default: 'Close',
						})}</Text
					>
				</svelte:fragment>
			</ControlsContainer>
		</div>
	{/if}
</div>
